import { formatLength, parseLength } from '@zerogap/engine';

export function money(cents: number | null | undefined, opts: { whole?: boolean; sign?: boolean } = {}): string {
  if (cents === null || cents === undefined || isNaN(cents)) return '—';
  const neg = cents < 0;
  const abs = Math.abs(cents);
  const text = opts.whole
    ? Math.round(abs / 100).toLocaleString('en-US')
    : (abs / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${neg ? '−' : opts.sign ? '+' : ''}$${text}`;
}

/** Decimal dollars as stored in a numeric column → cents. */
export function toCents(v: unknown): number {
  const n = Number(v);
  return isNaN(n) ? 0 : Math.round(n * 100);
}

export function hours(minutes: string | number | null | undefined, places = 1): string {
  const m = Number(minutes ?? 0);
  if (!m) return '0 h';
  return `${(m / 60).toFixed(places)} h`;
}

export function minutes(m: string | number | null | undefined): string {
  const n = Number(m ?? 0);
  return `${Number.isInteger(n) ? n : n.toFixed(1)} min`;
}

export function qty(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return Number.isInteger(n) ? String(n) : n.toFixed(Math.abs(n) < 10 ? 3 : 2).replace(/\.?0+$/, '');
}

/** Decimal inches → what the shop reads: 24 3/8". */
export function inches(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  return `${formatLength(String(v))}"`;
}

/** Typed text → decimal inches as text, or null when it is not a length. */
export function readLength(text: string): string | null {
  return parseLength(text)?.toDecimal(6) ?? null;
}

/** A calendar date (or a timestamp that is exactly midnight UTC, which is a date in disguise) reads the same in every time zone. */
function asDate(v: unknown): Date {
  const s = String(v);
  const day = /^(\d{4}-\d{2}-\d{2})(T00:00:00(\.0+)?(\+00:00|Z))?$/.exec(s);
  return day ? new Date(`${day[1]}T12:00:00`) : new Date(s);
}

export function shortDate(v: unknown): string {
  if (!v) return '—';
  const d = asDate(v);
  if (isNaN(d.getTime())) return '—';
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

/** A date the way the pipeline says it: Oct 16, Feb 1; with , Tue Oct 20. */
export function monthDay(v: unknown, weekday = false): string {
  if (!v) return '—';
  const d = asDate(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(weekday ? { weekday: 'short' } : {}) });
}

export function longDate(v: unknown): string {
  if (!v) return '—';
  const d = asDate(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function daysSince(v: unknown): number | null {
  if (!v) return null;
  const d = new Date(String(v));
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

/** An age that rolls forward from a birth date, never a typed number that goes stale. */
export function age(birthdate: unknown): number | null {
  if (!birthdate) return null;
  const b = new Date(`${String(birthdate).slice(0, 10)}T12:00:00`);
  if (isNaN(b.getTime())) return null;
  const now = new Date();
  let a = now.getFullYear() - b.getFullYear();
  if (now.getMonth() < b.getMonth() || (now.getMonth() === b.getMonth() && now.getDate() < b.getDate())) a--;
  return a;
}

export function contactName(c: Record<string, any> | null | undefined): string {
  if (!c) return '—';
  const person = [c.first_name, c.last_name].filter(Boolean).join(' ');
  return person || c.company || '(no name)';
}

export function title(s: string): string {
  return s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** The plural of an operation's unit, for "how many sheets =": /in → inches, /pc → pieces, /box → boxes, /ft → feet, each stays each. */
export function plural(unit: string | null | undefined): string {
  const u = String(unit || 'unit').replace(/^\//, '').trim();
  const known: Record<string, string> = { in: 'inches', pc: 'pieces', ft: 'feet', box: 'boxes', each: 'each', ea: 'each', hr: 'hours', min: 'minutes', 'sq ft': 'sq ft', 'lin ft': 'lin ft', job: 'jobs' };
  const k = known[u.toLowerCase()];
  if (k) return k;
  if (/s$/i.test(u)) return u; // already plural (minutes, trips)
  if (/(x|ch|sh)$/i.test(u)) return `${u}es`;
  if (/[^aeiou]y$/i.test(u)) return `${u.slice(0, -1)}ies`;
  return `${u}s`;
}
